import type { ChartBreakout } from '@g17eco/types/insight-custom-dashboard';
import type { UniversalTrackerModalServiceUtrv, ValueData } from '@g17eco/types/universalTrackerValue';
import { DATE, formatDateUTC } from '@utils/date';
import dayjs from 'dayjs';

export type GroupTableUtrv = Pick<UniversalTrackerModalServiceUtrv, 'effectiveDate' | 'valueData'>;

/** Diagnostic info about rows excluded during grouping — emitted as data, not via a
 * side-effect log, so callers can dedupe with React effects. Readonly so callers can't
 * push past the DROPPED_ROW_SAMPLE_LIMIT or mutate counts after the fact. */
export interface DropInfo {
  readonly missingGroupCount: number;
  readonly invalidValueCount: number;
  readonly droppedSamples: readonly string[];
}

export interface GroupedSeries {
  series: Array<{
    /** Raw breakout column code(s), joined — the stable group identity. */
    groupKey: string;
    /** Display label resolved from groupKey; presentation only, not guaranteed unique. */
    label: string;
    points: Array<{ period: string; value: number }>;
  }>;
  dropped: DropInfo;
}

export interface GroupTableOptions {
  resolveGroupLabel?: (rawValue: string) => string;
}

const findCellValue = (row: NonNullable<NonNullable<ValueData['table']>[number]>, code: string) =>
  row.find((cell) => cell.code === code)?.value;

const toNumber = (raw: unknown): number | null => {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    // isFinite rejects Infinity / -Infinity / NaN — Number('Infinity') passes the older
    // !isNaN check and would poison every later sum in the bucket.
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

// UTC month-start as a sortable lexicographic key. dayjs handles timezone-offset
// parsing — slicing the raw string would treat '2026-02-01T00:00:00+10:00' (Jan 31
// UTC) as Feb, splitting rows that share a UTC month from peers stored with Z.
export const toMonthStartKey = (effectiveDate: string): string | null => {
  const d = dayjs.utc(effectiveDate);
  return d.isValid() ? d.format('YYYY-MM-01') : null;
};

export const groupedSeriesToChartData = (series: GroupedSeries['series']): Array<Array<string | number | null>> => {
  const allPeriods = Array.from(
    new Set(series.flatMap((s) => s.points.map((p) => p.period)))
  ).sort();
  const header: Array<string | number | null> = ['Period', ...series.map((s) => s.label)];
  const valueByPeriod = series.map((s) => new Map(s.points.map((p) => [p.period, p.value])));
  const body: Array<Array<string | number | null>> = allPeriods.map((period) => [
    formatDateUTC(period, DATE.MONTH_YEAR_SHORT),
    ...valueByPeriod.map((periodValues) => periodValues.get(period) ?? null),
  ]);
  return [header, ...body];
};

// Cap on how many dropped-row examples to attach to the drop log — enough to
// debug a data-quality issue without an unbounded payload.
const DROPPED_ROW_SAMPLE_LIMIT = 5;

export const groupTableUtrvs = (
  utrvs: GroupTableUtrv[],
  breakout: ChartBreakout,
  valueColumnCode: string,
  options: GroupTableOptions = {}
): GroupedSeries => {
  const { resolveGroupLabel = (v) => v } = options;
  const buckets = new Map<string, { label: string; periods: Map<string, number> }>();
  let missingGroupCount = 0;
  let invalidValueCount = 0;
  const droppedSamples: string[] = [];

  for (const utrv of utrvs) {
    // effectiveDate is the period bucket key — a UTRV without one can't be placed.
    // Callers pre-filter these out, so this defensive skip is intentionally not
    // counted in the dropped-row totals below.
    if (!utrv.effectiveDate) {
      continue;
    }
    const periodKey = toMonthStartKey(utrv.effectiveDate);
    if (periodKey === null) {
      continue;
    }
    const rows = utrv.valueData?.table ?? [];
    for (const row of rows) {
      // Distinguish a missing cell (null/undefined) from a cell explicitly set to ''
      // — empty-string is a legitimate bucket value (e.g. an "unassigned" category).
      const keyParts: (string | null)[] = breakout.columns.map((c) => {
        const v = findCellValue(row, c.code);
        return v == null ? null : String(v);
      });
      if (keyParts.some((p) => p === null)) {
        missingGroupCount += 1;
        if (droppedSamples.length < DROPPED_ROW_SAMPLE_LIMIT) {
          droppedSamples.push(`(no group) @ ${utrv.effectiveDate}`);
        }
        continue;
      }
      // Identity is the raw code; the label is presentation only. Keying buckets by
      // the resolved label would silently merge distinct codes that share a name.
      const groupKeyParts = keyParts as string[];
      const groupKey = groupKeyParts.join(' / ');

      const rawValue = findCellValue(row, valueColumnCode);
      const value = toNumber(rawValue);
      if (value === null) {
        invalidValueCount += 1;
        if (droppedSamples.length < DROPPED_ROW_SAMPLE_LIMIT) {
          droppedSamples.push(`${groupKey} @ ${utrv.effectiveDate}: "${String(rawValue)}"`);
        }
        continue;
      }

      let bucket = buckets.get(groupKey);
      if (bucket === undefined) {
        bucket = { label: groupKeyParts.map(resolveGroupLabel).join(' / '), periods: new Map<string, number>() };
        buckets.set(groupKey, bucket);
      }
      bucket.periods.set(periodKey, (bucket.periods.get(periodKey) ?? 0) + value);
    }
  }

  return {
    series: Array.from(buckets.entries()).map(([groupKey, { label, periods }]) => ({
      groupKey,
      label,
      points: Array.from(periods.entries()).map(([period, value]) => ({ period, value })),
    })),
    // Surfacing drop info as data (not as a side-effect log) lets callers dedupe with
    // a React effect — see BreakoutChartWidget. Otherwise a fresh closure on every
    // parent re-render would re-fire identical Sentry events.
    dropped: { missingGroupCount, invalidValueCount, droppedSamples },
  };
};
